import { useQuery } from '@tanstack/react-query';

import { BaseChart } from '@/features/charts/components/base-chart';
import { ChartTypeSelector } from '@/features/charts/components/chart-type-selector';
import {
  AggregationIntervall,
  ChartType,
  AggregationType,
} from '@/features/charts/components/create-chart-type-selection';
import {
  ChartStats,
  CustomChartHeader,
} from '@/features/charts/components/custom-chart-header';
import { EmptyChartPlaceholder } from '@/features/charts/components/empty-chart-placeholder';
import {
  ChartData,
  ChartSettings,
  generateChartConfigAndData,
  generateColorVariants,
  generateMockChartData,
} from '@/features/charts/util/chart-util';
import { PeriodType } from '@/features/forecasts/api/get-kpi-forecasts';
import { KpiAnomaly } from '@/features/insights/api/detect-kpi-anomalies';
import { usePartners } from '@/features/partners/hooks/use-partners';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/shared/components/ui/card';
import { KpiForecast, KpiWithEntries } from '@/shared/types/api';
import { KpiType } from '@/shared/types/api';
import { KpiUnit } from '@/shared/types/kpi';
import { cn } from '@/shared/utils/cn';

export const ChartFromKpi = ({
  chartType,
  title,
  description,
  kpi,
  aggregationType = AggregationType.ACCUMULATED,
  aggregationInterval = 'monthly',
  // showAggregationControls = false,
  dimensions,
  chartClassName,
  onUpdateChartType,
  availableChartTypes,
  unit,
  forecastData,
  anomalyData,
  periodSelector,
  showStats = true,
  chartSettings,
}: {
  chartType: ChartType;
  title: string;
  description: string;
  kpi: KpiWithEntries;
  aggregationType?: AggregationType;
  aggregationInterval?: AggregationIntervall;
  showAggregationControls?: boolean;
  dimensions?: {
    height?: string;
    width?: string;
    minWidth?: string;
  };
  chartClassName?: string;
  onUpdateChartType?: (chartType: ChartType) => void;
  availableChartTypes?: ChartType[];
  unit: string | KpiUnit | null;
  forecastData?: Array<KpiForecast>;
  anomalyData?: Array<KpiAnomaly>;
  periodSelector?: React.ReactNode;
  periodType?: PeriodType;
  showStats?: boolean;
  chartSettings?: ChartSettings;
}) => {
  const colors = generateColorVariants(1);

  const { data: partners = [] } = usePartners();

  const { data: chartResult, isLoading: isChartLoading } = useQuery({
    queryKey: [
      'chartConfigAndData',
      kpi.id,
      kpi.entries?.length,
      aggregationType,
      aggregationInterval,
      chartType,
      forecastData?.length,
      anomalyData?.length,
      partners.length,
    ],
    queryFn: () =>
      generateChartConfigAndData({
        kpis: [kpi],
        aggregationType: aggregationType,
        aggregationIntervall: aggregationInterval,
        colors: colors,
        chartType: chartType,
        forecastData: forecastData,
        anomalyData: anomalyData,
        partners: partners,
      }),
  });

  const { data: mockChartResult, isLoading: isMockLoading } = useQuery({
    queryKey: ['mockChartData', chartType, aggregationType, kpi.max_value],
    queryFn: () =>
      generateMockChartData(
        chartType,
        aggregationType,
        kpi.max_value ?? undefined,
      ),
  });

  const chartData = chartResult?.chartData ?? [];
  const chartConfig = chartResult?.chartConfig ?? {};

  const isLoading = isChartLoading || isMockLoading;

  // Default dimensions
  const defaultDimensions = {
    height: 'h-full max-h-[500px]',
    width: 'w-full',
  };

  const finalDimensions = {
    height: dimensions?.height || defaultDimensions.height,
    width: dimensions?.width || defaultDimensions.width,
    minWidth: dimensions?.minWidth,
  };

  function determineEmpty(data: ChartData) {
    switch (aggregationType) {
      case AggregationType.ACCUMULATED:
        return data.every((item) => item.value === 0);
      case AggregationType.TIMELINE:
        return data.length === 0;
    }
  }

  const isEmpty = determineEmpty(chartData);

  // Only block rendering while loading - once loaded, always render
  // The chart component handles empty states internally
  const canRenderChart =
    !isLoading && (chartResult !== undefined || mockChartResult !== undefined);

  return (
    <div className={cn('relative', finalDimensions.height)}>
      <Card
        className={cn(
          `${finalDimensions.height} ${finalDimensions.width} ${finalDimensions.minWidth} border-none`,
          isEmpty && 'opacity-50 blur-lg',
        )}
        variant="chart"
      >
        <CardHeader>
          <CustomChartHeader
            title={isEmpty ? '' : title}
            description={isEmpty ? '' : description}
            children={
              <>
                {availableChartTypes && onUpdateChartType && (
                  <ChartTypeSelector
                    currChartType={chartType}
                    onUpdateChartType={onUpdateChartType}
                    availableChartTypes={availableChartTypes}
                  />
                )}
                {periodSelector}
              </>
            }
          />
        </CardHeader>

        <CardContent>
          {canRenderChart ? (
            <BaseChart
              chartType={chartType}
              chartSettings={chartSettings}
              chartResult={
                isEmpty && mockChartResult
                  ? mockChartResult
                  : { chartData, chartConfig }
              }
              colors={colors}
              aggregationType={aggregationType}
              kpiType={kpi.type as KpiType}
              maxValue={kpi.max_value ?? undefined}
              chartClassName={chartClassName}
              unit={unit}
            />
          ) : (
            <div
              className={cn('flex items-center justify-center', chartClassName)}
            />
          )}
        </CardContent>
        {showStats && (
          <CardFooter className="flex flex-row items-center justify-center gap-4">
            <ChartStats
              chartData={chartData}
              aggregationType={aggregationType}
              maxValue={kpi.max_value ? kpi.max_value : undefined}
              unit={unit}
            />
          </CardFooter>
        )}
      </Card>

      {isEmpty && !chartSettings?.hidePlaceholderIfEmpty && (
        <EmptyChartPlaceholder />
      )}
    </div>
  );
};
